import React, { useState } from 'react'
import { Card, CardBody, CardFooter, Col, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import CustomToast from '../../../utils/CustomToast';
import DownloadIcon from 'assets/img/bis/download-icon.png';
import axios from 'axios';
import { AiFillInfoCircle } from 'react-icons/ai';
import useSiteCookies from '../useSiteCookies';

const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB; frontend-only cap, images only

const FileUploaderModal = ({ show, onHide, instance, id, onData }) => {
    const instanceType = instance.instance;
    const instanceId = instance.id;

    const { currentUser, token } = useSiteCookies();

    const [formStatus, setFormStatus] = useState(false);
    const [uploadedFileError, setUploadedFileError] = useState(false);
    const [uploadedFile, setUploadedFile] = useState('Upload file here');

    const [item, setItem] = useState({ name: '', value: '' });

    const allowedFileTypes = {
        image: ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'],
        video: ['video/mp4', 'video/quicktime', 'video/x-msvideo'], // quicktime = mov, x-msvideo = avi
        audio: ['audio/mpeg', 'audio/wav'], // mpeg = mp3
        document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv']
    };

    const uploadInstanceLabel = {
        document: 'DOCUMENT',
        photo_1: 'PHOTO 1',
        photo_2: 'PHOTO 2',
        image: 'IMAGE',
        video: 'VIDEO',
        audio: 'AUDIO',
    };

    const hiddenFileInputPdf = React.useRef(null);

    const postFile = async (data) => {
        return await axios.post(
            process.env.REACT_APP_API_ENDPOINT + `category/question/upload`, data, {
            params: {
                user_id: currentUser,
                token: token
            }
        });
    }

    const handleClickFile = () => {
    hiddenFileInputPdf.current.click();
    };

    const handleDragEnter = e => {
        e.preventDefault();
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDragLeave = e => {
        e.preventDefault();
    };

    const handleDrop = e => {
        e.preventDefault();
    
        const droppedFiles = e.dataTransfer.files;
        const totalFilesCount = droppedFiles.length;
    
        if (totalFilesCount > 1) {
            const message = "This field accepts only 1!";
            CustomToast.warning(message);
            return;
        }
    
        if (formStatus)
            return;
    
        setFormStatus(true);
    
        if (droppedFiles[0]) {
            const file = droppedFiles[0];
            const size = file.size;
        
            if (instanceType === 'image') {
                imageValidations(file);
            } else if (instanceType === 'video') {
                videoValidations(file);
            } else if (instanceType === 'audio') {
                audioValidations(file);
            } else if (instanceType === 'document') {
                documentValidations(file);
            }
        }
    };

    const handleChangeFiles = ({ target }) => {
        if (target.files < 1 || !target.validity.valid) {
          return;
        }

        setFormStatus(true);
    
        if (target.files[0]) {
            const file = target.files[0];
            const size = file.size;
        
            if (instanceType === 'image') {
                imageValidations(file);
            } else if (instanceType === 'video') {
                videoValidations(file);
            } else if (instanceType === 'audio') {
                audioValidations(file);
            } else if (instanceType === 'document') {
                documentValidations(file);
            }
        }

        target.value = '';
    };


    const validateFile = (file, type) => {
        if (type === 'image' && allowedFileTypes.image.includes(file.type) && file.size > MAX_IMAGE_SIZE) {
            setFormStatus(false);
            CustomToast.warning('Image must be 20MB or smaller.');
            return;
        }

        if (allowedFileTypes[type].includes(file.type)) {
            submitFile(file);
            setUploadedFileError(false);
        } else {
            setFormStatus(false);
            CustomToast.warning(`Please upload a valid ${type} file!`);
        }
    };
    
    const imageValidations = file => validateFile(file, 'image');
    const videoValidations = file => validateFile(file, 'video');
    const audioValidations = file => validateFile(file, 'audio');
    const documentValidations = file => validateFile(file, 'document');

    const submitFile = (file) => {
        setFormStatus(true);
        const dataArray = new FormData();
        dataArray.append('file', file);
        
        postFile(dataArray).then(response => {
            const status = response.data.status;
            if (status === 'Success') {
                setFormStatus(false);
                setUploadedFile(file.name);

                const data = response.data.data.url;
                if (data) {
                    setItem({ name: 'File', value: data })
                }
            } else {
                CustomToast.defaultError();
                setFormStatus(false);
            }
        }).catch(() => {  
            CustomToast.defaultError();
            setFormStatus(false);
        });
    }

    const handleUpload = () => {
        if (item.value === '') {
            setUploadedFileError(true);
            setFormStatus(false);
            return;
        } else {
            onData({ target: item });
            onHide();
            setUploadedFile('Upload file here');
        }
    }

    const toggleHide = () => {
        onHide();
        setItem({ name: '', value: '' });
        setUploadedFile('Upload file here');
        setUploadedFileError(false);
    }

    return (
        <>
            <Modal
                isOpen={show}
                className="modal-preview"
                fade={false}
                centered
                id="edit-judge-modal"
            >
            <ModalHeader className="text-left">
                UPLOAD {uploadInstanceLabel[instanceType]}
                {instanceType === 'image' && (
                    <div className="bis-tooltip p-0 ml-2" style={{ border: 'none', background: 'transparent', verticalAlign: 'middle' }}>
                        <AiFillInfoCircle />
                        <span className="bis-tooltiptext" style={{ width: '220px', marginLeft: '-110px', textTransform: 'none' }}>Maximum image size is 20MB</span>
                    </div>
                )}
                {!formStatus && (
                <button
                    type="button"
                    className="close react-modal-close"
                    data-dismiss="modal"
                    aria-label="Close"
                    onClick={() => toggleHide()}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
                )}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody className="card_container">
                                <Label
                                    onClick={handleClickFile}
                                    onDragEnter={handleDragEnter}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={`${uploadedFileError
                                        ? 'download_icon form-invalid'
                                        : 'download_icon'
                                        }`}
                                    style={
                                        formStatus
                                        ? { cursor: 'not-allowed', opacity: '0.5' }
                                        : {}
                                    }
                                >
                                    <img src={DownloadIcon} />
                                    <br />
                                    {uploadedFile}
                                </Label>
                                {uploadedFileError ? (
                                    <p role="alert" className="invalid-feedback">
                                        File is required
                                    </p>
                                ) : null}
                                <input
                                    type="file"
                                    ref={hiddenFileInputPdf}
                                    accept={
                                        instanceType === 'image'
                                        // ? '.jpg, .jpeg, .png, .gif, .bmp, .webp, .svg'
                                        ? '.jpeg, .png, .jpg, .gif'
                                        : instanceType === 'video'
                                        // ? '.mp4, .avi, .mov, .wmv, .flv, .mkv, .webm'
                                        ? '.mp4, .mov, .avi'
                                        : instanceType === 'audio'
                                        // ? '.mp3, .wav, .ogg, .aac, .flac, .m4a'
                                        ? '.mp3, .wav'
                                        : instanceType === 'document'
                                        // ? '.pdf, .doc, .docx, .odt, .rtf, .txt, .xls, .xlsx, .csv, .ods, .ppt, .pptx, .odp, .zip, .rar, .7z, .tar, .gz,  .html, .htm, .css, .js, .json, .xml, .php, .py, .java, .c, .cpp'
                                        ? '.pdf, .doc, .docx, .xlsx, .csv'
                                        : 'image/*'
                                    }
                                    onChange={handleChangeFiles}
                                    style={{ display: 'none' }}
                                    required
                                    disabled={formStatus}
                                />
                            </CardBody>
                            <CardFooter className="text-right">
                                {!formStatus && (
                                <button
                                    type="button"
                                    className="btn btn-main btn-main-alt mr-2"
                                    data-dismiss="modal"
                                    onClick={() => toggleHide()}
                                >
                                    CLOSE
                                </button>
                                )}
                                {!formStatus ? (
                                <button
                                    type="button"
                                    className="newbuttontheme w-25"
                                    onClick={handleUpload}
                                >
                                    SAVE
                                </button>
                                ) : (
                                <button
                                    type="button"
                                    className="newbuttontheme w-25"
                                >
                                    UPLOADING...
                                </button>
                                )}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </ModalBody>
            </Modal>
        </>
    )
}

export default FileUploaderModal